import React from 'react';
import { clsx } from 'clsx';
import { TRADE_STATUS_COLORS, TRADE_STATUS_LABELS } from '@/types';
import type { EffectiveTradeStatus } from '@/types/trades.types';

export type PillTone = 'accent' | 'warn' | 'pos' | 'neg' | 'neutral';

const PILL_TONE_CLASS: Record<PillTone, string> = {
  accent: 'bg-lr-accent-weak text-lr-accent',
  warn: 'bg-lr-warn-weak text-lr-warn',
  pos: 'bg-lr-pos-weak text-lr-pos',
  // LR-436 D4: text-lr-neg (#dc2626) on bg-lr-neg-weak is 4.29:1 — fails WCAG AA.
  // Use the darker danger-700 (#b91c1c → 5.74:1) for the pill foreground only;
  // the shared lr.neg token is left intact (it is consumed as a figure color in
  // ~7 other components where the contrast is fine).
  neg: 'bg-lr-neg-weak text-danger-700',
  neutral: 'bg-gray-100 text-gray-700',
};

const STATUS_FALLBACK = { bg: 'bg-gray-100', text: 'text-gray-800' };

/** "pending_open_settlement" -> "Pending Open Settlement". */
function humanizeStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface StatusPillProps {
  /** Trade (effective) lifecycle status — maps to TRADE_STATUS_COLORS + the
   *  canonical TRADE_STATUS_LABELS label (LR-497). */
  status?: EffectiveTradeStatus;
  /** Explicit tone when not a lifecycle status (direction pill, warn chip). */
  tone?: PillTone;
  /** Leading status dot (live indicator). */
  dot?: boolean;
  /** Explicit label; overrides the humanized status label. */
  children?: React.ReactNode;
  className?: string;
  /** Native tooltip (e.g. STATUS_TOOLTIPS[status]) shown on hover (LR-544). */
  title?: string;
}

/**
 * Rounded status/category pill. Covers the trade lifecycle badge (via
 * `status`), the direction chip, and warn chips (via `tone`). Replaces the
 * inline `rounded-full …` span duplicated across the trade pages.
 */
export function StatusPill({ status, tone, dot = false, children, className, title }: StatusPillProps) {
  const statusColors = status ? (TRADE_STATUS_COLORS[status] ?? STATUS_FALLBACK) : null;
  const colorClass = statusColors
    ? clsx(statusColors.bg, statusColors.text)
    : PILL_TONE_CLASS[tone ?? 'neutral'];
  // LR-497: prefer the canonical label; humanizeStatus stays the fallback for any
  // status not in the map. An explicit `children` label still wins.
  const label = children ?? (status ? (TRADE_STATUS_LABELS[status] ?? humanizeStatus(status)) : null);

  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        colorClass,
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />}
      {label}
    </span>
  );
}
